import { resolveColorVariable } from '@/lib/derive-content-tone';
import type { ColorSettings } from '@/types/shared';

export const COLOR_FIELDS = [
  { key: 'headerBackground', label: 'Header Background', defaultColor: '#1e3a5f' },
  { key: 'headerLinks', label: 'Header Links', defaultColor: '#ffffff' },
  { key: 'siteBackground', label: 'Site Background', defaultColor: '#ffffff' },
  { key: 'siteText', label: 'Site Text', defaultColor: '#1a1a1a' },
  { key: 'siteBackgroundAlt', label: 'Site Background Alternate', defaultColor: '#f8fafc' },
  { key: 'siteTextAlt', label: 'Site Text Alternate', defaultColor: '#64748b' },
  { key: 'primaryButtonBackground', label: 'Primary Button Background', defaultColor: '#1e3a5f' },
  { key: 'primaryButtonText', label: 'Primary Button Text', defaultColor: '#ffffff' },
  {
    key: 'secondaryButtonBackground',
    label: 'Secondary Button Background',
    defaultColor: '#f1f5f9',
  },
  { key: 'secondaryButtonText', label: 'Secondary Button Text', defaultColor: '#1a1a1a' },
  { key: 'cardBackground', label: 'Card Background', defaultColor: '#ffffff' },
  { key: 'cardText', label: 'Card Text', defaultColor: '#1a1a1a' },
  { key: 'borders', label: 'Borders', defaultColor: '#e2e8f0' },
  { key: 'footerBackground', label: 'Footer Background', defaultColor: '#1e3a5f' },
  { key: 'footerText', label: 'Footer Text', defaultColor: '#e2e8f0' },
  { key: 'footerLinks', label: 'Footer Links', defaultColor: '#ffffff' },
] as const;

export type ColorField = (typeof COLOR_FIELDS)[number];
export type ColorKey = ColorField['key'];

export const DEFAULT_COLOR_VALUES: ColorSettings = COLOR_FIELDS.reduce(
  (acc, { key, defaultColor }) => {
    acc[key] = defaultColor;
    return acc;
  },
  {} as ColorSettings,
);

export function normalizeColorValues(
  overrides?: Partial<ColorSettings> | null,
  defaults: ColorSettings = DEFAULT_COLOR_VALUES,
): ColorSettings {
  const normalized: ColorSettings = { ...defaults };

  if (!overrides) return normalized;

  for (const { key } of COLOR_FIELDS) {
    const value = (overrides as Record<string, string>)[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      normalized[key as keyof ColorSettings] = value;
    }
  }

  return normalized;
}

// Card/section templates can store a background/border color as either a literal hex or a
// theme-linked CSS variable (e.g. 'var(--muted)') so it keeps tracking the site's colors.
// Resolve the variable to its current hex purely for display in a color picker — the
// stored value (and its live theme-tracking) is left untouched unless the user picks a
// new color themselves.
export function resolveDisplayColor(
  raw: string | undefined,
  colors: Partial<ColorSettings> | null | undefined,
  fallback: string | undefined,
): string {
  if (!raw) return fallback ?? '#ffffff';
  if (!colors) return raw;
  return resolveColorVariable(raw, colors) ?? raw;
}
